import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Alert, Button, Code, Group, Stack, Text } from '@mantine/core';
import { IconAlertTriangle, IconRefresh } from '@tabler/icons-react';
import { captureException } from '../lib/sentry';

interface Props {
  children: ReactNode;
  /** Optional reset key — when it changes, the boundary resets. Useful for
   *  router transitions: pass `location.pathname` so navigation re-mounts. */
  resetKey?: string;
}

interface State {
  err: Error | null;
}

/**
 * Class-based error boundary so a render error in any descendant doesn't
 * unmount the entire vendor shell. The shell header + navigation stay
 * visible; only the route content gets the error fallback.
 *
 * Errors are forwarded to {@link captureException} so they reach Sentry
 * once a DSN is configured.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { err: null };

  static getDerivedStateFromError(err: Error): State {
    return { err };
  }

  override componentDidCatch(err: Error, info: ErrorInfo): void {
    captureException(err);
    // eslint-disable-next-line no-console
    console.error('[error-boundary]', err, info.componentStack);
  }

  override componentDidUpdate(prev: Props): void {
    if (prev.resetKey !== this.props.resetKey && this.state.err) {
      this.setState({ err: null });
    }
  }

  reset = (): void => {
    this.setState({ err: null });
  };

  override render(): ReactNode {
    if (!this.state.err) return this.props.children;
    return (
      <Stack maw={720} mx="auto" mt="xl">
        <Alert
          color="red"
          variant="light"
          icon={<IconAlertTriangle size={20} />}
          title="Something went wrong"
        >
          <Text size="sm">
            The page hit an unexpected error. The rest of the app is still working — you can
            navigate elsewhere via the sidebar, or retry to reload just this section.
          </Text>
        </Alert>
        <Code block style={{ whiteSpace: 'pre-wrap', maxHeight: 200, overflow: 'auto' }}>
          {this.state.err.message}
        </Code>
        <Group>
          <Button leftSection={<IconRefresh size={16} />} variant="default" onClick={this.reset}>
            Retry
          </Button>
        </Group>
      </Stack>
    );
  }
}
